import { useEffect, useState, useRef } from "react";
import { useTour } from "@/contexts/TourContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, X, Sparkles } from "lucide-react";

export default function TourOverlay() {
  const {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    nextStep,
    prevStep,
    skipTour
  } = useTour();

  const [spotlightStyle, setSpotlightStyle] = useState({});
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef(null);

  // Calculate spotlight and tooltip positions
  useEffect(() => {
    if (!isActive || !currentStep) {
      setIsVisible(false);
      return;
    }

    // Small delay for animation
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    if (!currentStep.target) {
      // Center modal for welcome/complete screens
      setSpotlightStyle({});
      setTooltipStyle({
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        maxWidth: "450px"
      });
      return () => clearTimeout(timer);
    }

    // Find the target element
    const targetEl = document.querySelector(currentStep.target);
    if (!targetEl) {
      // If target not found, show centered
      setSpotlightStyle({});
      setTooltipStyle({
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        maxWidth: "400px"
      });
      return () => clearTimeout(timer);
    }

    // Get target position
    const rect = targetEl.getBoundingClientRect();
    const padding = 8;

    // Set spotlight position
    setSpotlightStyle({
      position: "fixed",
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
      borderRadius: "8px"
    });

    // Calculate tooltip position based on specified position
    const tooltipWidth = 320;
    const tooltipHeight = 200;
    const margin = 16;

    let top, left;
    const position = currentStep.position || "right";

    switch (position) {
      case "right":
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + margin;
        // Check if tooltip goes off screen
        if (left + tooltipWidth > window.innerWidth - margin) {
          left = rect.left - tooltipWidth - margin;
        }
        break;
      case "left":
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - margin;
        if (left < margin) {
          left = rect.right + margin;
        }
        break;
      case "top":
        top = rect.top - tooltipHeight - margin;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        if (top < margin) {
          top = rect.bottom + margin;
        }
        break;
      case "bottom":
        top = rect.bottom + margin;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      default:
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + margin;
    }

    // Ensure tooltip stays within viewport
    top = Math.max(margin, Math.min(top, window.innerHeight - tooltipHeight - margin));
    left = Math.max(margin, Math.min(left, window.innerWidth - tooltipWidth - margin));

    setTooltipStyle({
      position: "fixed",
      top,
      left,
      width: tooltipWidth
    });

    // Scroll target into view if needed
    targetEl.scrollIntoView({ behavior: "smooth", block: "center" });

    return () => clearTimeout(timer);
  }, [isActive, currentStep, currentStepIndex]);

  if (!isActive || !currentStep) return null;

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;
  const isCenterModal = !currentStep.target;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="tour-overlay">
      {/* Dark overlay with spotlight hole */}
      <div className="absolute inset-0 transition-opacity duration-300"
           style={{ opacity: isVisible ? 1 : 0 }}>
        {/* SVG mask for spotlight effect */}
        <svg className="absolute inset-0 w-full h-full">
          <defs>
            <mask id="spotlight-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {spotlightStyle.width && (
                <rect
                  x={spotlightStyle.left}
                  y={spotlightStyle.top}
                  width={spotlightStyle.width}
                  height={spotlightStyle.height}
                  rx="8"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.75)"
            mask="url(#spotlight-mask)"
          />
        </svg>

        {/* Spotlight border glow */}
        {spotlightStyle.width && (
          <div
            className="absolute border-2 border-primary shadow-lg shadow-primary/50 pointer-events-none"
            style={{
              ...spotlightStyle,
              boxShadow: "0 0 0 4px rgba(59, 130, 246, 0.3), 0 0 20px rgba(59, 130, 246, 0.5)"
            }}
          />
        )}
      </div>

      {/* Tooltip Card */}
      <Card
        ref={tooltipRef}
        className={`z-[101] shadow-2xl border-primary/50 bg-background transition-all duration-300 ${
          isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
        } ${isCenterModal ? "p-8" : "p-4"}`}
        style={tooltipStyle}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h3 className={`font-bold text-foreground ${isCenterModal ? "text-xl" : "text-base"}`}>
                {currentStep.title}
              </h3>
              {!isCenterModal && (
                <p className="text-xs text-muted-foreground">
                  Step {currentStepIndex + 1} of {totalSteps}
                </p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 -mt-1 -mr-1"
            onClick={skipTour}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Description */}
        <p className={`text-muted-foreground mb-4 ${isCenterModal ? "text-base" : "text-sm"}`}>
          {currentStep.description}
        </p>

        {/* Progress bar */}
        <div className="w-full h-1 bg-muted rounded-full mb-4 overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${((currentStepIndex + 1) / totalSteps) * 100}%` }}
          />
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={skipTour}
            className="text-muted-foreground"
          >
            Skip tour
          </Button>
          
          <div className="flex items-center gap-2">
            {!isFirstStep && (
              <Button
                variant="outline"
                size="sm"
                onClick={prevStep}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            <Button
              size="sm"
              onClick={nextStep}
              className="min-w-[80px]"
            >
              {isLastStep ? (
                "Finish"
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
